import React, { useState } from 'react';
import { spacingMobile, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { Download, Copy, Check, Sliders, Image as ImageIcon, Palette, ChevronDown, ChevronUp, Brain, Pen, Code, Rocket, TrendingUp, Eye, Sun, Moon, Info, X } from 'lucide-react';
import MobileRangeSlider from '../mobile/MobileRangeSlider';
import DualColorPicker from '../ui/ColorPicker';
import Listbox from '../ui/Listbox';
import { aspectRatios } from '../../constants/aspectRatios';
import { asciiStyles } from '../../constants/asciiStyles';
import Button from '../ui/Button';
import MobilePreviewModal from '../modals/MobilePreviewModal';

const MobileLayout = ({
  onDownload,
  onCopy,
  isDownloading,
  downloaded,
  copied,
  summary,
  mode,
  modeToggle,
  currentPhase,
  onPhaseChange,
  phases,
  phaseKeys,
  asciiPreview,
  creativePromptView,
  settings,
  hasUploadedMedia,
  onDensityChange,
  onColumnsChange,
  onAspectRatioChange,
  onGammaChange,
  onContrastChange,
  onCharacterCountChange,
  onAsciiStyleChange,
  onGifBackgroundColorChange,
  onGifForegroundColorChange,
  onDimensionModeChange,
  onCustomWidthChange,
  onCustomHeightChange,
  gifProgress = 0,
  isGeneratingGif = false,
  gifComplete = false
}) => {
  const { colors, isDark, toggleTheme } = useTheme();

  // Determine if we're in any downloading state
  const isAnyDownloading = isDownloading || isGeneratingGif;
  const isAnyComplete = downloaded || gifComplete;
  const [activeTab, setActiveTab] = useState('basic');
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const { density, columns, aspectRatio, gamma, contrast, characterCount, asciiStyle, characterSet, gifBackgroundColor, gifForegroundColor, dimensionMode, customWidth, customHeight } = settings;

  const styleOptions = Object.values(asciiStyles);

  const controlsHeight = controlsExpanded ? '50%' : '48px';

  return (
    <div className="mobile-layout" style={{
      display: 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.background,
      overflowX: 'hidden'
    }}>
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>
      {/* TOP BAR - Fixed */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '56px',
        background: colors.backgroundPanel,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: spacingMobile.sm,
        padding: `0 ${spacingMobile.md}`,
        zIndex: 100,
        boxSizing: 'border-box'
      }}>
        <Button
          onClick={onDownload}
          variant="outline"
          size="sm"
          disabled={isAnyDownloading}
          style={{
            position: 'relative',
            overflow: 'hidden',
            minWidth: isGeneratingGif ? '80px' : 'auto'
          }}
        >
          {isGeneratingGif && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${gifProgress}%`,
              height: '100%',
              background: 'rgba(0,0,0,0.1)',
              transition: 'width 0.3s ease-out'
            }} />
          )}
          {isAnyComplete ? <Check size={14} /> : isAnyDownloading ? <Download size={14} style={{animation: 'bounce 0.6s infinite'}} /> : <Download size={14} />}
          {isGeneratingGif && <span style={{ marginLeft: '4px', fontSize: '0.625rem' }}>{Math.round(gifProgress)}%</span>}
        </Button>
        <Button onClick={onCopy} variant="outline" size="sm">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </Button>
        {/* Theme Toggle */}
        <Button onClick={toggleTheme} variant="outline" size="sm">
          {isDark ? <Sun size={14} /> : <Moon size={14} />}
        </Button>

        {/* Info Button */}
        <Button onClick={() => setIsInfoModalOpen(true)} variant="outline" size="sm" style={{ marginLeft: 'auto' }}>
          <Info size={14} />
        </Button>
      </div>

      {/* MAIN CONTENT - Scrollable */}
      <div style={{
        position: 'fixed',
        top: '56px',
        left: 0,
        right: 0,
        bottom: (mode === 'preset' || (mode === 'creative' && !creativePromptView)) ? controlsHeight : '0',
        overflowY: controlsExpanded ? 'hidden' : 'auto',
        overflowX: 'clip',
        WebkitOverflowScrolling: 'touch',
        transition: 'bottom 0.3s ease-out',
        width: '100vw'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          padding: controlsExpanded ? 0 : spacingMobile.md,
          paddingBottom: controlsExpanded ? 0 : spacingMobile.md,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          boxSizing: 'border-box',
          gap: controlsExpanded ? 0 : spacingMobile.md,
          overflowX: 'clip'
        }}>
          {/* Mode Toggle */}
          {!controlsExpanded && modeToggle}

          {/* Creative Prompt */}
          {mode === 'creative' && creativePromptView ? (
            <div>{creativePromptView}</div>
          ) : (
            <>
              {/* Phase Selector - Horizontal with Icons */}
              {mode === 'preset' && !controlsExpanded && (
                <div style={{
                  position: 'sticky',
                  top: '56px',
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  background: colors.background,
                  paddingTop: spacingMobile.sm,
                  paddingBottom: spacingMobile.sm,
                  marginTop: `-${spacingMobile.sm}`,
                  marginLeft: `-${spacingMobile.md}`,
                  marginRight: `-${spacingMobile.md}`,
                  paddingLeft: spacingMobile.md,
                  paddingRight: spacingMobile.md,
                  marginBottom: spacingMobile.sm,
                  display: 'flex',
                  gap: spacingMobile.sm,
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  width: '100vw',
                  maxWidth: '100vw'
                }}>
                  {(() => {
                    const phaseIcons = {
                      think: Brain,
                      design: Pen,
                      develop: Code,
                      launch: Rocket,
                      scale: TrendingUp
                    };

                    return phaseKeys.map(phase => {
                      const Icon = phaseIcons[phase];
                      const isActive = currentPhase === phase;
                      return (
                        <button
                          key={phase}
                          onClick={() => onPhaseChange(phase)}
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: `${spacingMobile.sm}`,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            color: isActive ? colors.background : colors.foreground,
                            background: isActive ? colors.foreground : colors.backgroundOffset,
                            border: 'none',
                            borderRadius: borderRadius.md,
                            minHeight: '56px',
                            textTransform: 'capitalize',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Icon size={20} strokeWidth={2.5} />
                          <span>{phases[phase].name}</span>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}

              {/* Live Preview - Full Screen Split */}
              <div
                  style={{
                    margin: 0,
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    background: colors.backgroundPanel,
                    boxSizing: 'border-box',
                    borderRadius: controlsExpanded ? 0 : borderRadius.md,
                    border: controlsExpanded ? 'none' : `1px solid ${colors.border}`
                  }}
                >
                  <div
                    onClick={() => setIsPreviewModalOpen(true)}
                    style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}>
                      <div style={{
                        transform: controlsExpanded
                          ? 'scale(0.85)'
                          : (aspectRatio === '16:9' || aspectRatio === '21:9'
                              ? 'scale(2.5)'
                              : aspectRatio === '9:16'
                                ? 'scale(6)'
                                : aspectRatio === '3:4'
                                  ? 'scale(4.5)'
                                  : 'scale(4)'),
                        transition: 'transform 0.3s ease-out'
                      }}>
                        {asciiPreview}
                      </div>
                  </div>
                  {!controlsExpanded && (
                    <button
                      onClick={() => setIsPreviewModalOpen(true)}
                      style={{
                        position: 'absolute',
                        bottom: spacingMobile.md,
                        right: spacingMobile.md,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: colors.background,
                        background: colors.foreground,
                        padding: `${spacingMobile.sm} ${spacingMobile.md}`,
                        borderRadius: borderRadius.md,
                        display: 'flex',
                        alignItems: 'center',
                        gap: spacingMobile.sm,
                        border: 'none',
                        cursor: 'pointer',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s',
                        outline: 'none'
                      }}
                      onTouchStart={(e) => {
                        e.currentTarget.style.transform = 'scale(0.95)';
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <Eye size={14} strokeWidth={2.5} />
                      <span>Full Size</span>
                    </button>
                  )}
                </div>

            </>
          )}
        </div>
      </div>

      {/* BOTTOM CONTROLS - Collapsible */}
      {(mode === 'preset' || (mode === 'creative' && !creativePromptView)) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: controlsHeight,
          background: colors.backgroundPanel,
          borderTop: `2px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'height 0.3s ease-out',
          zIndex: 100,
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)'
        }}>
          {/* Tab Bar with Collapse Button */}
          <div style={{
            display: 'flex',
            borderBottom: controlsExpanded ? `1px solid ${colors.border}` : 'none',
            height: '48px',
            flexShrink: 0,
            background: controlsExpanded ? colors.backgroundPanel : colors.backgroundOffset
          }}>
            {/* Collapse/Expand Button */}
            <button
              onClick={() => setControlsExpanded(!controlsExpanded)}
              style={{
                width: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRight: `1px solid ${colors.border}`,
                color: colors.foreground,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {controlsExpanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
            </button>

            {/* Tabs */}
            {[
              { id: 'basic', icon: Sliders, label: 'Basic' },
              { id: 'visual', icon: ImageIcon, label: 'Visual' },
              { id: 'colors', icon: Palette, label: 'Colors' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (!controlsExpanded) setControlsExpanded(true);
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  background: 'transparent',
                  border: 'none',
                  color: activeTab === tab.id ? colors.foreground : colors.foregroundSecondary,
                  fontSize: '0.6875rem',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  borderBottom: `2px solid ${activeTab === tab.id ? colors.foreground : 'transparent'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onTouchStart={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.03)';
                  }
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <tab.icon size={18} strokeWidth={activeTab === tab.id ? 2.5 : 2} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content - Only visible when expanded */}
          {controlsExpanded && (
            <div style={{
              flex: 1,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              padding: spacingMobile.md,
              background: colors.backgroundPanel
            }}>
              {activeTab === 'basic' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacingMobile.lg, paddingBottom: spacingMobile.xxl }}>
                  <Listbox
                    label="Style"
                    value={asciiStyle}
                    onChange={onAsciiStyleChange}
                    options={styleOptions}
                  />
                  <MobileRangeSlider min={0.3} max={1.5} step={0.1} value={density} onChange={onDensityChange} label={`Presence: ${density.toFixed(1)}`} />
                  <MobileRangeSlider min={40} max={200} step={10} value={columns} onChange={onColumnsChange} label={`Resolution: ${columns}`} />
                  <MobileRangeSlider min={2} max={characterSet.length} step={1} value={Math.min(characterCount, characterSet.length)} onChange={onCharacterCountChange} label={`Characters: ${characterCount}`} />
                </div>
              )}

              {activeTab === 'visual' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacingMobile.lg, paddingBottom: spacingMobile.xxl }}>
                  <MobileRangeSlider min={0.5} max={2.0} step={0.1} value={gamma} onChange={onGammaChange} label={`Tonality: ${gamma.toFixed(1)}`} />
                  <MobileRangeSlider min={0.5} max={2.0} step={0.1} value={contrast} onChange={onContrastChange} label={`Intensity: ${contrast.toFixed(1)}`} />

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: spacingMobile.sm, color: colors.foreground }}>Dimensions</label>

                    {/* Mode Toggle - PNG/GIF style */}
                    <div style={{
                      display: 'inline-flex',
                      gap: spacingMobile.xs,
                      padding: spacingMobile.xs,
                      background: colors.backgroundPanel,
                      borderRadius: borderRadius.sm,
                      marginBottom: spacingMobile.md
                    }}>
                      {['Aspect Ratio', 'Custom'].map((mode) => {
                        const modeValue = mode === 'Aspect Ratio' ? 'aspectRatio' : 'custom';
                        return (
                          <button
                            key={mode}
                            onClick={() => onDimensionModeChange(modeValue)}
                            style={{
                              padding: `${spacingMobile.xs} ${spacingMobile.md}`,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: dimensionMode === modeValue ? colors.background : colors.foreground,
                              background: dimensionMode === modeValue ? colors.foreground : 'transparent',
                              border: 'none',
                              borderRadius: borderRadius.sm,
                              cursor: 'pointer',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>

                    {/* Conditional Content */}
                    {dimensionMode === 'aspectRatio' ? (
                      // Aspect Ratio Mode
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacingMobile.sm }}>
                        {Object.entries(aspectRatios).map(([key, { label }]) => (
                          <button
                            key={key}
                            onClick={() => onAspectRatioChange(key)}
                            style={{
                              minHeight: '44px',
                              fontSize: '0.8125rem',
                              fontWeight: 500,
                              color: aspectRatio === key ? colors.background : colors.foreground,
                              background: aspectRatio === key ? colors.foreground : 'transparent',
                              border: `1px solid ${aspectRatio === key ? colors.foreground : colors.border}`,
                              borderRadius: borderRadius.sm,
                              cursor: 'pointer',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      // Custom Pixels Mode - Figma style
                      <div style={{ display: 'flex', gap: spacingMobile.sm }}>
                        {/* Width Input */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <label style={{
                            position: 'absolute',
                            left: spacingMobile.sm,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: colors.foregroundSecondary,
                            pointerEvents: 'none',
                            userSelect: 'none'
                          }}>
                            W
                          </label>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={customWidth}
                            onChange={(e) => onCustomWidthChange(parseInt(e.target.value) || 1920)}
                            min={320}
                            max={3840}
                            step={1}
                            style={{
                              width: '100%',
                              minHeight: '44px',
                              paddingLeft: '30px',
                              paddingRight: spacingMobile.sm,
                              fontSize: '0.8125rem',
                              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                              color: colors.foreground,
                              background: colors.backgroundOffset,
                              border: `1px solid ${colors.border}`,
                              borderRadius: borderRadius.sm,
                              outline: 'none',
                              WebkitAppearance: 'none'
                            }}
                          />
                        </div>

                        {/* Height Input */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <label style={{
                            position: 'absolute',
                            left: spacingMobile.sm,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: colors.foregroundSecondary,
                            pointerEvents: 'none',
                            userSelect: 'none'
                          }}>
                            H
                          </label>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={customHeight}
                            onChange={(e) => onCustomHeightChange(parseInt(e.target.value) || 1080)}
                            min={180}
                            max={2160}
                            step={1}
                            style={{
                              width: '100%',
                              minHeight: '44px',
                              paddingLeft: '30px',
                              paddingRight: spacingMobile.sm,
                              fontSize: '0.8125rem',
                              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                              color: colors.foreground,
                              background: colors.backgroundOffset,
                              border: `1px solid ${colors.border}`,
                              borderRadius: borderRadius.sm,
                              outline: 'none',
                              WebkitAppearance: 'none'
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'colors' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacingMobile.lg, paddingBottom: spacingMobile.xxl }}>
                  <DualColorPicker
                    key={mode}
                    label="Colors"
                    backgroundColor={gifBackgroundColor}
                    foregroundColor={gifForegroundColor}
                    onBackgroundColorChange={onGifBackgroundColorChange}
                    onForegroundColorChange={onGifForegroundColorChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      <MobilePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        asciiPreview={asciiPreview}
      />

      {/* Info Modal */}
      {isInfoModalOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000
            }}
            onClick={() => setIsInfoModalOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              maxHeight: '80vh',
              background: colors.backgroundPanel,
              borderTopLeftRadius: borderRadius.xl,
              borderTopRightRadius: borderRadius.xl,
              border: `1px solid ${colors.border}`,
              borderBottom: 'none',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Handle */}
            <div
              style={{
                width: '48px',
                height: '4px',
                borderRadius: '999px',
                background: colors.border,
                margin: `${spacingMobile.md} auto ${spacingMobile.sm}`,
                flexShrink: 0
              }}
            />

            {/* Header */}
            <div style={{
              padding: `${spacingMobile.md} ${spacingMobile.lg}`,
              paddingTop: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: colors.foreground
              }}>
                Significa ASCII
              </h2>
              <button
                onClick={() => setIsInfoModalOpen(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.sm,
                  color: colors.foregroundSecondary,
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: `0 ${spacingMobile.lg}`,
              paddingBottom: spacingMobile.lg,
              overflowY: 'auto',
              flex: 1
            }}>
              <p style={{
                margin: `0 0 ${spacingMobile.md} 0`,
                fontSize: '1rem',
                fontWeight: 500,
                color: colors.foregroundSecondary,
                letterSpacing: '0.025em'
              }}>
                Create your own ASCII patterns
              </p>

              <div style={{
                fontSize: '0.875rem',
                lineHeight: '1.6',
                color: colors.foregroundSecondary,
                fontWeight: 400
              }}>
                <p style={{ margin: 0 }}>
                  This system generates ASCII patterns moving through our process: Think, Design, Develop, Launch, and Scale. Submit any prompt and each set of characters will somehow incorporate an egg, the symbol at the base of our logo. These visual representations of all things Significa support our brand comms, but it's open for you to try and play around with, too.
                </p>
              </div>

              {/* Recently Updated Badge */}
              <div style={{
                marginTop: spacingMobile.lg,
                paddingTop: spacingMobile.lg,
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  background: colors.backgroundOffset,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.sm
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#10b981',
                    boxShadow: '0 0 12px rgba(16, 185, 129, 0.8), 0 0 6px rgba(16, 185, 129, 1)',
                    animation: 'pulse-glow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }} />
                  <span style={{
                    color: colors.foregroundSecondary,
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}>
                    Recently updated
                  </span>
                </div>
              </div>

              <div style={{
                marginTop: spacingMobile.md,
                fontSize: '0.75rem',
                color: colors.foregroundSecondary
              }}>
                Made with love by{' '}
                <a
                  href="https://significa.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.foreground,
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  Significa
                </a>
              </div>
            </div>

            <style>
              {`
                @keyframes pulse-glow {
                  0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                    box-shadow: 0 0 12px rgba(16, 185, 129, 0.8), 0 0 6px rgba(16, 185, 129, 1);
                  }
                  50% {
                    opacity: 0.4;
                    transform: scale(1.3);
                    box-shadow: 0 0 20px rgba(16, 185, 129, 0.6), 0 0 10px rgba(16, 185, 129, 0.8);
                  }
                }
              `}
            </style>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileLayout;
